/**
 * The deck desktop screen's single Escape precedence order.
 *
 * Escape is claimed by five different things on this screen, and before this
 * module each claimed it with its own independent `keydown` listener. That
 * arrangement has no way to express "only the innermost one acts": two
 * listeners both matching a press both run, so one press could close an
 * editor AND clear the card selection, and neither listener could know the
 * other had fired. This module is the one place the order is decided.
 *
 * **The order, outermost-yields-to-innermost:**
 *
 *  1. `overlay`      — a modal, sheet, popover or menu is open. The ladder
 *                      does NOT close it; it yields, and the overlay's own
 *                      handler does the closing. Yielding rather than closing
 *                      is deliberate: each overlay has its own cancel
 *                      semantics (some discard a draft, some do not) and
 *                      re-implementing them here would be a second, drifting
 *                      copy. All the ladder owes them is to not act underneath.
 *  2. `edit-session`  — a card is open for EDITING. Escape ends the edit
 *                      session and returns that card to reading. It must not
 *                      also clear the selection: one press, one level.
 *  3. `search`        — the in-deck card search box has text. Escape clears
 *                      the text, restoring the full list.
 *  4. `deck-mode`     — the deck-wide editing mode is on. Escape turns it off.
 *  5. `selection`     — a card is selected for reading. Escape returns the
 *                      pane to its resting empty state.
 *  6. `none`          — nothing to dismiss; the press is left alone.
 *
 * Rungs 3 and 4 sit BETWEEN the two card-level rungs on purpose. Both are
 * screen-wide states the reader switched on deliberately, and a reader who
 * presses Escape while a filter is narrowing the list means "drop the
 * filter" far more often than "drop the card I am reading". Putting them
 * below the selection would also make the selection unreachable by Escape
 * whenever a search was active, which is precisely the dead end this order
 * exists to avoid.
 *
 * Rung 1 is a single boolean rather than a list of overlays because the
 * ladder's only interest is whether SOMETHING is layered above the screen.
 * Which overlay it is, and what closing it means, belongs to that overlay.
 *
 * A more specific owner that has already handled the press — the active
 * card's own key handler, the field-type popover, a field drag being
 * cancelled — signals that by calling `preventDefault()`, and the caller
 * checks `defaultPrevented` before consulting this ladder at all. That check
 * is not modelled here: it is about a native event, not about screen state,
 * and this function stays pure so the order can be unit-tested without a DOM.
 */

/** Which level of the screen a single Escape press resolves to. */
export type DeckEscapeRung =
  | 'overlay'
  | 'edit-session'
  | 'search'
  | 'deck-mode'
  | 'selection'
  | 'none'

/** The screen state the order is decided from. One flag per rung, in rung
 *  order, so a reader can check this type against the list above directly. */
export interface DeckEscapeState {
  /** Any modal, sheet, popover or menu layered over the screen. */
  overlayOpen: boolean
  /** A card is open for editing (single-card Edit, deck mode, or the unsaved
   *  draft) — as opposed to open for reading. */
  editSessionOpen: boolean
  /** The in-deck card search box holds text. */
  searchNonEmpty: boolean
  /** The deck-wide editing mode is on. */
  deckModeOn: boolean
  /** A card is selected in the two-pane list. */
  cardSelected: boolean
}

/**
 * Resolves one Escape press to exactly one rung. Total and side-effect free —
 * the caller performs the action for whichever rung comes back.
 */
export function resolveDeckEscapeRung(state: DeckEscapeState): DeckEscapeRung {
  if (state.overlayOpen) return 'overlay'
  if (state.editSessionOpen) return 'edit-session'
  if (state.searchNonEmpty) return 'search'
  if (state.deckModeOn) return 'deck-mode'
  if (state.cardSelected) return 'selection'
  return 'none'
}
